import React, { useState, useEffect, Suspense } from 'react';
import { captureReferralFromUrl } from './services/referralLink';
import {Routes, Route, Navigate, useNavigate} from 'react-router-dom';
import { lazyWithReload } from './utils/lazyWithReload';
import { Toaster } from 'react-hot-toast';
import { ConfirmHost } from './components/ui/ConfirmDialog';
import { FounderMessageModal } from './components/FounderMessageModal';
import { hasAdminPortalAccess } from './services/permissions';
import {getRememberedPortal} from './services/portalPreference';

// Dev-only role switcher — lazy-imported so it's completely tree-shaken in production.
// Vite statically analyzes `import.meta.env.DEV` and removes the dead branch at build time.
const DevRoleSwitcher: React.LazyExoticComponent<React.FC> | null = import.meta.env.DEV
  ? lazyWithReload(() => import('./dev/DevRoleSwitcher'))
  : null;
import { useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AssignmentDeepLink } from './components/AssignmentDeepLink';
import { MaterialDeepLink } from './components/MaterialDeepLink';
import { ClassInviteDeepLink } from './components/ClassInviteDeepLink';
import { LoadingScreen } from './components/LoadingScreen';
// ── Lazy-loaded components ──────────────────────────────────────────────────
// Each of these ships as a separate JS chunk and only downloads when the user
// actually visits the route or opens the view. Named exports are unwrapped
// through `.then(m => ({ default: m.Foo }))` since React.lazy() expects a
// module with a default export.

// Public / unauth routes — not needed once a user is signed in
const StudioLandingPage = lazyWithReload(() => import('./components/StudioLandingPage').then(m => ({ default: m.StudioLandingPage })));
const LoginScreen = lazyWithReload(() => import('./components/LoginScreen').then(m => ({ default: m.LoginScreen })));
const RegisterScreen = lazyWithReload(() => import('./components/RegisterScreen').then(m => ({ default: m.RegisterScreen })));
const ForgotPasswordScreen = lazyWithReload(() => import('./components/ForgotPasswordScreen').then(m => ({ default: m.ForgotPasswordScreen })));
const JoinClassPage = lazyWithReload(() => import('./components/JoinClassPage').then(m => ({ default: m.JoinClassPage })));
const ParentPortalView = lazyWithReload(() => import('./components/ParentPortalView').then(m => ({ default: m.ParentPortalView })));
const JoinSchoolPage = lazyWithReload(() => import('./components/JoinSchoolPage').then(m => ({ default: m.JoinSchoolPage })));
const AcceptInvitePage = lazyWithReload(() => import('./components/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })));
const SubAccessPage = lazyWithReload(() => import('./components/SubAccessPage').then(m => ({ default: m.SubAccessPage })));
const SchoolsLandingPage = lazyWithReload(() => import('./components/SchoolsLandingPage').then(m => ({ default: m.SchoolsLandingPage })));
const LegalInfoModal = lazyWithReload(() => import('./components/LegalInfoModal').then(m => ({ default: m.LegalInfoModal })));

// Role-specific apps — a teacher never needs the admin or student bundles,
// and vice versa
// The signed-in teacher shell. Lazy — a static import would drag Gradebook,
// Dashboard, all of Firestore and the daily games into the landing page's chunk.
const TeacherApp = lazyWithReload(() => import('./TeacherApp').then(m => ({ default: m.TeacherApp })));
const AdminDashboard = lazyWithReload(() => import('./components/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const StudentPortal = lazyWithReload(() => import('./components/StudentPortal').then(m => ({ default: m.StudentPortal })));

// Heavy TeacherApp views — loaded only when the teacher opens them
import { recordUserActivity } from './services/userActivity';
import {initAnalytics, shouldAskForAnalyticsConsent} from './services/analytics';
import { AnalyticsConsentBanner } from './components/AnalyticsConsentBanner';
const StudentApp: React.FC = () => {
  return <StudentPortal />;
};

const LegalPage: React.FC<{ tab: 'privacy' | 'eula' | 'security' }> = ({ tab }) => {
  const navigate = useNavigate();
  return (
    <LegalInfoModal
      isOpen={true}
      onClose={() => navigate('/')}
      initialTab={tab}
    />
  );
};

const AdminApp: React.FC = () => {
  const { logout } = useAuth();
  return <AdminDashboard onLogout={logout} />;
};

/**
 * Resolves which root route a signed-in user should land on after
 * login / accept-invite / generic redirect. Centralized because four
 * Route guards used to inline the same branching and missed the
 * school-association case: a VP whose userProfile.role is 'teacher'
 * (because the role field is platform-wide, not school-scoped) was
 * being routed to /app instead of /admin.
 */
const homeRouteFor = (
  profile: { uid?: string; role: string; schoolAssociations?: Array<{ role: string }> } | null,
): string => {
  if (!profile) return '/login';
  if (profile.role === 'student') return '/student';
  // Non-admins only have one side, so there's nothing to remember.
  if (!hasAdminPortalAccess(profile.role, profile.schoolAssociations)) return '/app';
  // Admins have two homes. Return them to whichever they were last using —
  // logging out on the teacher dashboard used to land them back on /admin.
  // /admin stays the default for a first visit. Checked AFTER the access test,
  // so a stale 'teacher' can never route someone somewhere they can't render.
  return getRememberedPortal(profile.uid) === 'teacher' ? '/app' : '/admin';
};

// An invite code can arrive on any entry point. Read it once, at the door.
captureReferralFromUrl(typeof window === 'undefined' ? '' : window.location.search);

const App: React.FC = () => {
  const { currentUser, userProfile, loading, pendingGoogleRole } = useAuth();

  // Analytics: students are NEVER tracked (anonymous/nickname sessions are students
  // too), and nobody is tracked without an explicit yes. See services/analytics.ts.
  const isStudent = userProfile?.role === 'student' || !!currentUser?.isAnonymous;
  // `loading` goes false BEFORE a signed-in user's profile arrives (App's own routes
  // handle `currentUser && !userProfile` with a LoadingScreen). Deciding then would
  // read an email-authed student as a non-student and — on a device that had
  // consented — inject the tag before we learn who they are. Never decide until the
  // role is actually known.
  const roleResolved = !loading && (!currentUser || !!currentUser.isAnonymous || !!userProfile);
  const [askConsent, setAskConsent] = useState(false);
  useEffect(() => {
    if (!roleResolved) return;
    /**
     * The role goes IN, rather than being stamped afterwards. initAnalytics is
     * what injects the tag, and the tag's own `config` call fires the
     * page_view — so a role set after this lands behind a hit that has already
     * gone, leaving every page_view unsegmentable. A student resolves to null
     * inside, where the tag is also killed for them.
     */
    initAnalytics({ isStudent, role: userProfile?.role });
    setAskConsent(shouldAskForAnalyticsConsent({ isStudent }));
  }, [roleResolved, isStudent, userProfile?.role]);

  // Stamp last-active (throttled to once/day) so the owner's active-users metric can
  // count real usage — including students, whom analytics deliberately never tracks.
  useEffect(() => {
    if (userProfile?.uid) recordUserActivity(userProfile.uid);
  }, [userProfile?.uid]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
    {askConsent && <AnalyticsConsentBanner onDecided={() => setAskConsent(false)} />}
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route path="/" element={
        currentUser?.isAnonymous ? (
          // Anonymous (nickname) student — go to student portal
          <Navigate to="/student" replace />
        ) : currentUser ? (
          // Logged in users get redirected to their portal
          userProfile ? (
            <Navigate to={homeRouteFor(userProfile)} replace />
          ) : pendingGoogleRole ? (
            <Navigate to="/login" replace />
          ) : (
            <LoadingScreen />
          )
        ) : (
          // Not logged in - show landing page
          <StudioLandingPage />
        )
      } />
      <Route path="/login" element={
        currentUser?.isAnonymous ? (
          <Navigate to="/student" replace />
        ) : currentUser ? (
          userProfile ? (
            <Navigate to={(() => { const dl = sessionStorage.getItem('deepLink'); if (dl) { sessionStorage.removeItem('deepLink'); return dl; } return homeRouteFor(userProfile); })()} replace />
          ) : pendingGoogleRole ? (
            <LoginScreen />
          ) : (
            <LoadingScreen />
          )
        ) : (
          <LoginScreen />
        )
      } />
      <Route path="/register" element={
        currentUser?.isAnonymous ? (
          <Navigate to="/student" replace />
        ) : currentUser ? (
          userProfile ? (
            <Navigate to={homeRouteFor(userProfile)} replace />
          ) : pendingGoogleRole ? (
            <Navigate to="/login" replace />
          ) : (
            <LoadingScreen />
          )
        ) : (
          <RegisterScreen />
        )
      } />
      <Route path="/forgot-password" element={
        currentUser?.isAnonymous ? (
          <Navigate to="/student" replace />
        ) : currentUser ? (
          userProfile ? (
            <Navigate to={homeRouteFor(userProfile)} replace />
          ) : pendingGoogleRole ? (
            <Navigate to="/login" replace />
          ) : (
            <LoadingScreen />
          )
        ) : (
          <ForgotPasswordScreen />
        )
      } />

      {/* Join Class page - for nickname-only students (no Firebase Auth required) */}
      <Route path="/join-class" element={<JoinClassPage />} />
      {/* Public read-only parent portal (magic link — token is the credential) */}
      <Route path="/p/:token" element={<ParentPortalView />} />

      {/* Invitation acceptance — public, token-gated */}
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* For School Boards landing page */}
      <Route path="/schools" element={<SchoolsLandingPage onNavigate={path => window.location.href = path} />} />

      {/* Public legal pages */}
      <Route path="/privacy" element={<LegalPage tab="privacy" />} />
      <Route path="/terms" element={<LegalPage tab="eula" />} />

      {/* Public sub-plan access — no auth, the URL token IS the auth */}
      <Route path="/sub/:token" element={<SubAccessPage />} />

      {/* Join School — teachers only */}
      <Route path="/join-school" element={
        <ProtectedRoute requiredRole="teacher">
          <JoinSchoolPage />
        </ProtectedRoute>
      } />

      <Route path="/admin/*" element={
        <ProtectedRoute requiredRole="board_admin">
          <AdminApp />
        </ProtectedRoute>
      } />

      <Route path="/app/*" element={
        <ProtectedRoute requiredRole="teacher">
          <TeacherApp />
        </ProtectedRoute>
      } />

      <Route path="/student/*" element={
        <ProtectedRoute requiredRole="student">
          <StudentApp />
        </ProtectedRoute>
      } />

      {/* Class invite link — the join code as a URL. See ClassInviteDeepLink. */}
      <Route path="/c/:classId" element={<ClassInviteDeepLink />} />
      <Route path="/a/:classId/:assessmentId" element={<AssignmentDeepLink />} />
      <Route path="/m/:classId/:postId" element={<MaterialDeepLink />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    <Toaster
      position="top-right"
      toastOptions={{
        className: '',
        style: {
          borderRadius: '12px',
          background: '#1f2937',
          color: '#fff',
          fontSize: '14px',
          padding: '12px 16px',
        },
        success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
        error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
      }}
    />
    <ConfirmHost />
    {/* Founder message — shown once to teachers and board admins only */}
    {currentUser && !currentUser.isAnonymous &&
      (userProfile?.role === 'teacher' || userProfile?.role === 'board_admin') &&
      <FounderMessageModal />}
    {DevRoleSwitcher && (
      <Suspense fallback={null}>
        <DevRoleSwitcher />
      </Suspense>
    )}
    </>
  );
};

export default App;
